import { useState, useEffect } from "react";
import LogoPowarticle from "./Logo-powarticle.svg";

/* ─── FONTS & GLOBAL CSS ─────────────────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&f[]=satoshi@300,400,500,700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    .ring, .ring-in, .ring:focus, .ring-in:focus { outline: none !important; box-shadow: none !important; border: none !important; }

    :root {
      --orange: #FA600A;
      --orange-hover: #e55509;
      --green: #1DAF61;
      --red: #FB3737;
      --blue: #3b82f6;
      --f-display: 'Clash Display', sans-serif;
      --f-body: 'Satoshi', sans-serif;
      --f-mono: 'DM Mono', monospace;
    }

    .lm {
      --bg: #F4F4F2;
      --card: #FFFFFF;
      --border: #E8E8E8;
      --text: #1a1a1a;
      --text2: #555;
      --text3: #999;
      --input-bg: #F9F9F9;
      --input-border: #E0E0E0;
      --pill-nav: #EFEFEF;
    }
    .dm {
      --bg: #141414;
      --card: #1E1E1E;
      --border: #2A2A2A;
      --text: #F5F5F5;
      --text2: #BBBBBB;
      --text3: #666;
      --input-bg: #252525;
      --input-border: #333;
      --pill-nav: #252525;
    }

    body { background: var(--bg); }

    .app {
      min-height: 100vh;
      background: var(--bg);
      font-family: var(--f-body);
      color: var(--text);
      transition: background 0.3s, color 0.3s;
      padding-bottom: 80px;
    }

    .nav {
      position: sticky; top: 0; z-index: 200;
      height: 66px;
      background: var(--card);
      border-bottom: 1px solid var(--border);
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 32px;
    }

    .step-bar {
      display: flex; align-items: center; gap: 10px;
      font-size: 12px; font-weight: 500; color: var(--text3);
      letter-spacing: 0.04em;
    }
    .step-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--border); transition: background 0.25s; }
    .step-dot.active { background: var(--orange); }

    .theme-pill {
      display: flex; background: var(--pill-nav);
      border-radius: 999px; padding: 4px; gap: 2px;
    }
    .theme-btn {
      background: transparent; border: none;
      border-radius: 999px; width: 34px; height: 34px;
      cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: all 0.22s; color: var(--text3);
    }
    .theme-btn.active { background: var(--orange); color: #fff; box-shadow: 0 2px 10px rgba(250,96,10,0.4); }

    .arr {
      width: 44px; height: 44px; border-radius: 50%;
      border: 1.5px solid var(--border);
      background: var(--card);
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
      color: var(--text2); flex-shrink: 0;
    }
    .arr:hover { border-color: var(--orange); background: var(--orange); color: #fff; transform: scale(1.08); box-shadow: 0 4px 18px rgba(250,96,10,0.3); }
    .arr.fill { background: var(--orange); border-color: var(--orange); color: #fff; box-shadow: 0 3px 14px rgba(250,96,10,0.35); }
    .arr.fill:hover { background: var(--orange-hover); transform: scale(1.1); box-shadow: 0 6px 22px rgba(250,96,10,0.45); }
    .arr.lg { width: 54px; height: 54px; }
    .arr:disabled { opacity: 0.3; cursor: not-allowed; transform: none !important; box-shadow: none !important; }

    .back-btn {
      display: inline-flex; align-items: center; gap: 10px;
      background: none; border: none; cursor: pointer;
      font-family: var(--f-body); font-size: 13px; font-weight: 500;
      color: var(--text3); transition: color 0.2s; padding: 0;
    }
    .back-btn:hover { color: var(--text); }

    .cta-btn {
      display: inline-flex; align-items: center; gap: 12px;
      background: var(--orange); border: none; color: #fff;
      padding: 0 10px 0 24px; height: 52px;
      border-radius: 999px; cursor: pointer;
      font-family: var(--f-body); font-size: 15px; font-weight: 600;
      letter-spacing: 0.01em; transition: all 0.22s;
    }
    .cta-btn:hover { background: var(--orange-hover); transform: translateY(-1px); box-shadow: 0 6px 24px rgba(250,96,10,0.4); }
    .cta-btn:disabled { opacity: 0.35; cursor: not-allowed; transform: none; box-shadow: none; }
    .cta-btn.green { background: var(--green); }
    .cta-btn.green:hover { background: #18a056; box-shadow: 0 6px 24px rgba(29,175,97,0.4); }
    .cta-circle {
      width: 34px; height: 34px; border-radius: 50%;
      background: rgba(255,255,255,0.25);
      display: flex; align-items: center; justify-content: center;
    }

    .ghost-btn {
      display: inline-flex; align-items: center; gap: 8px;
      background: transparent; border: 1.5px solid var(--border); color: var(--text2);
      padding: 10px 18px; border-radius: 999px; cursor: pointer;
      font-family: var(--f-body); font-size: 13px; font-weight: 500; transition: all 0.2s;
    }
    .ghost-btn:hover { border-color: var(--orange); color: var(--orange); }
    .ghost-btn:disabled { opacity: 0.4; cursor: not-allowed; }

    .copy-btn {
      background: var(--input-bg); border: 1px solid var(--input-border); color: var(--text3);
      padding: 5px 14px; border-radius: 999px; cursor: pointer;
      font-size: 11px; font-weight: 600; font-family: var(--f-body);
      letter-spacing: 0.04em; transition: all 0.2s; white-space: nowrap;
    }
    .copy-btn:hover { border-color: var(--orange); color: var(--orange); }
    .copy-btn.done { background: var(--green); border-color: var(--green); color: #fff; }

    .wrap { max-width: 760px; margin: 0 auto; padding: 40px 20px; }

    .card {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 18px; padding: 28px; margin-bottom: 14px;
    }

    .lbl {
      display: block; font-size: 10px; font-weight: 700;
      letter-spacing: 0.12em; text-transform: uppercase;
      color: var(--text3); margin-bottom: 7px; font-family: var(--f-body);
    }

    .inp {
      width: 100%; background: var(--input-bg);
      border: 1.5px solid var(--input-border); border-radius: 12px;
      padding: 13px 16px; color: var(--text);
      font-size: 14px; font-family: var(--f-body); font-weight: 400;
      outline: none; transition: border-color 0.2s; appearance: none;
    }
    .inp:focus { border-color: var(--orange); }
    .inp::placeholder { color: var(--text3); }

    .sec { font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--orange); margin-bottom: 14px; font-family: var(--f-body); }

    .tag { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 600; margin: 3px; font-family: var(--f-body); }
    .tag-o { background: rgba(250,96,10,0.1); color: var(--orange); border: 1px solid rgba(250,96,10,0.2); }
    .tag-g { background: rgba(29,175,97,0.1); color: var(--green); border: 1px solid rgba(29,175,97,0.2); }
    .tag-n { background: var(--input-bg); color: var(--text3); border: 1px solid var(--input-border); }
    .tag-b { background: rgba(59,130,246,0.1); color: var(--blue); border: 1px solid rgba(59,130,246,0.2); }

    .len-pill {
      flex: 1; border: 1.5px solid var(--input-border);
      background: var(--input-bg); color: var(--text3);
      border-radius: 12px; padding: 10px 6px;
      text-align: center; cursor: pointer;
      font-family: var(--f-body); font-size: 12px; font-weight: 600; transition: all 0.2s;
    }
    .len-pill.active { background: var(--orange); border-color: var(--orange); color: #fff; box-shadow: 0 2px 10px rgba(250,96,10,0.25); }

    .tone-pill {
      flex: 1; border: 1.5px solid var(--input-border);
      background: var(--input-bg); color: var(--text3);
      border-radius: 12px; padding: 10px 8px;
      text-align: center; cursor: pointer;
      font-family: var(--f-body); font-size: 12px; font-weight: 600; transition: all 0.2s;
      white-space: nowrap;
    }
    .tone-pill.active { background: var(--orange); border-color: var(--orange); color: #fff; box-shadow: 0 2px 10px rgba(250,96,10,0.25); }

    .div { border: none; border-top: 1px solid var(--border); margin: 22px 0; }

    .ring { width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 8px; outline: none !important; border: none !important; box-shadow: none !important; }
    .ring-in { width: 46px; height: 46px; border-radius: 50%; background: var(--card); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; font-family: var(--f-display); outline: none; border: none; }

    .idea-c {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 16px; padding: 20px 22px; margin-bottom: 10px;
      display: flex; gap: 16px; align-items: flex-start;
      transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
    }
    .idea-c:hover { border-color: var(--orange); box-shadow: 0 4px 22px rgba(250,96,10,0.08); transform: translateY(-1px); }

    .art h1 { font-family: var(--f-display); font-size: 22px; font-weight: 700; color: var(--orange); margin: 18px 0 10px; line-height: 1.3; }
    .art h2 { font-family: var(--f-display); font-size: 17px; font-weight: 600; color: var(--text); margin: 16px 0 8px; }
    .art h3 { font-family: var(--f-display); font-size: 14px; font-weight: 600; color: var(--text2); margin: 12px 0 6px; }
    .art p  { color: var(--text2); line-height: 1.78; margin-bottom: 12px; font-size: 14px; }
    .art ul { padding-left: 20px; color: var(--text2); margin: 8px 0; }
    .art li { margin-bottom: 6px; font-size: 14px; }
    .art a  { color: var(--orange); text-underline-offset: 3px; }

    .err { background: rgba(251,55,55,0.07); border: 1px solid rgba(251,55,55,0.22); border-radius: 12px; padding: 12px 16px; color: var(--red); font-size: 13px; font-weight: 500; display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
    .success-box { background: rgba(29,175,97,0.07); border: 1px solid rgba(29,175,97,0.25); border-radius: 12px; padding: 14px 18px; color: var(--green); font-size: 13px; font-weight: 500; margin-bottom: 18px; }

    .modal-overlay {
      position: fixed; inset: 0; z-index: 500;
      background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center; padding: 20px;
    }
    .modal {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 22px; padding: 32px; width: 100%; max-width: 480px;
      box-shadow: 0 24px 80px rgba(0,0,0,0.2);
      animation: up 0.3s cubic-bezier(0.22,1,0.36,1) both;
    }

    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes up { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
    .up  { animation: up 0.4s cubic-bezier(0.22,1,0.36,1) both; }
    .u1  { animation: up 0.4s 0.07s cubic-bezier(0.22,1,0.36,1) both; }
    .u2  { animation: up 0.4s 0.14s cubic-bezier(0.22,1,0.36,1) both; }

    @keyframes bar-slide {
      0%   { transform: scaleX(0); opacity: 0.4; }
      50%  { transform: scaleX(1); opacity: 1; }
      100% { transform: scaleX(0); opacity: 0.4; }
    }
    @keyframes fade-pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
    .loader-bar {
      height: 2px; border-radius: 999px; background: var(--orange);
      transform-origin: left center;
      animation: bar-slide 1.8s cubic-bezier(0.4,0,0.2,1) infinite;
    }
    .loader-dot {
      width: 5px; height: 5px; border-radius: 50%; background: var(--orange);
      animation: fade-pulse 1.4s ease-in-out infinite;
    }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
    select.inp option { background: var(--card); color: var(--text); }

    .seo-bar-track { height: 8px; border-radius: 999px; background: var(--border); position: relative; overflow: visible; }
    .seo-bar-fill { height: 100%; border-radius: 999px; transition: width 0.4s, background 0.3s; }
    .seo-zone { position: absolute; top: 0; height: 100%; border-radius: 0; opacity: 0.18; }
    .seo-check-item { display: flex; align-items: flex-start; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--border); font-size: 13px; color: var(--text2); line-height: 1.4; }
    .seo-check-item:last-child { border-bottom: none; }
    .seo-bullet { width: 11px; height: 11px; border-radius: 50%; flex-shrink: 0; margin-top: 2px; }
    .serp-preview { background: var(--card); border: 1px solid var(--border); border-radius: 14px; padding: 20px 22px; }
    .serp-url { font-size: 12px; color: #3c8a47; font-family: arial,sans-serif; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .serp-title { font-size: 18px; color: #1a0dab; font-family: arial,sans-serif; line-height: 1.3; margin-bottom: 4px; }
    .serp-desc { font-size: 13px; color: #545454; font-family: arial,sans-serif; line-height: 1.55; }
    .dm .serp-title { color: #8ab4f8; }
    .dm .serp-desc { color: #bdc1c6; }
    .dm .serp-url { color: #81c995; }
    .seo-overall { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; font-family: var(--f-body); }
    .seo-overall.good { background: rgba(29,175,97,0.12); color: #1DAF61; border: 1px solid rgba(29,175,97,0.25); }
    .seo-overall.ok   { background: rgba(250,96,10,0.10); color: #FA600A; border: 1px solid rgba(250,96,10,0.22); }
    .seo-overall.bad  { background: rgba(251,55,55,0.09); color: #FB3737; border: 1px solid rgba(251,55,55,0.22); }

    .sc-kpi { background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 20px 22px; flex: 1; min-width: 130px; transition: border-color 0.2s, transform 0.2s; }
    .sc-kpi:hover { border-color: var(--orange); transform: translateY(-1px); }
    .sc-kpi-val { font-family: var(--f-display); font-size: 28px; font-weight: 700; letter-spacing: -0.03em; line-height: 1; margin-bottom: 4px; }
    .sc-kpi-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text3); margin-bottom: 10px; }
    .sc-row { background: var(--card); border: 1px solid var(--border); border-radius: 14px; padding: 16px 20px; margin-bottom: 10px; transition: border-color 0.2s, transform 0.2s; }
    .sc-row:hover { border-color: var(--orange); transform: translateY(-1px); }
    .sc-badge-indexed { background: rgba(29,175,97,0.12); color: #1DAF61; border: 1px solid rgba(29,175,97,0.25); font-size:10px; font-weight:700; padding:3px 10px; border-radius:999px; white-space:nowrap; }
    .sc-badge-pending { background: rgba(250,96,10,0.10); color: #FA600A; border: 1px solid rgba(250,96,10,0.22); font-size:10px; font-weight:700; padding:3px 10px; border-radius:999px; white-space:nowrap; }
    .sc-badge-waiting { background: rgba(153,153,153,0.12); color: var(--text3); border: 1px solid var(--border); font-size:10px; font-weight:700; padding:3px 10px; border-radius:999px; white-space:nowrap; }
    .sc-connect-card { background: var(--card); border: 1.5px dashed var(--border); border-radius: 22px; padding: 52px 32px; text-align: center; max-width: 480px; margin: 60px auto 0; }
    .sc-tab { padding: 7px 18px; border-radius: 999px; border: 1.5px solid var(--border); background: transparent; cursor: pointer; font-family: var(--f-body); font-size: 12px; font-weight: 600; color: var(--text3); transition: all 0.2s; }
    .sc-tab.active { background: var(--orange); border-color: var(--orange); color: #fff; }
    .range-pill { padding:5px 14px; border-radius:999px; border:1.5px solid var(--border); background:transparent; cursor:pointer; font-family:var(--f-body); font-size:11px; font-weight:600; color:var(--text3); transition:all 0.2s; }
    .range-pill.active { background:var(--input-bg); border-color:var(--orange); color:var(--orange); }
  `}</style>
);

const NOW_DATE = "Mars 2026";
const NOW_YEAR = "2026";
const TONES_ART = ["Expert","Marketing","Pédagogique","Storytelling","Inspirant"];
const LENGTHS = [{label:"Court",value:"court",sub:"~500 mots"},{label:"Moyen",value:"moyen",sub:"~1000 mots"},{label:"Long",value:"long",sub:"~1500 mots"}];
const COUNTRIES = ["France","Belgique","Suisse","Canada","Maroc","Tunisie","Sénégal","Côte d'Ivoire","International"];
const LANGUAGES = ["Français","Anglais","Espagnol","Allemand","Italien","Portugais","Arabe"];
const DIFF_C = {Facile:"#1DAF61",Moyen:"#FA600A",Difficile:"#FB3737"};
const INTENT_C = {Informationnelle:"#3b82f6",Commerciale:"#FA600A",Transactionnelle:"#1DAF61",Navigationnelle:"#a855f7"};
const STORE = "seo_blog_v6";
const MODEL_FAST = "claude-haiku-4-5-20251001";
const MODEL_MAIN = "claude-sonnet-4-20250514";

async function callClaude(usr, sys, max = 2048, model = MODEL_FAST, onChunk) {
  const r = await fetch("/api/claude.php", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model, max_tokens: max, system: sys, messages: [{ role: "user", content: usr }] })
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e?.error?.message || `HTTP ${r.status}`);
  }

  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "", fullText = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6).trim();
      if (!data) continue;
      try {
        const evt = JSON.parse(data);
        if (evt.type === "content_block_delta" && evt.delta?.type === "text_delta") {
          fullText += evt.delta.text;
          onChunk?.(fullText);
        } else if (evt.type === "error") {
          throw new Error(evt.error?.message || "Stream error");
        }
      } catch (err) {
        if (err.message?.includes("Stream error")) throw err;
      }
    }
  }
  return fullText;
}

async function streamClaude(usr, sys, onChunk, max=3000) {
  // Use non-streaming to avoid timeout issues in artifact environment
  const r = await fetch("/api/claude.php", {
    method:"POST", 
    headers:{
      "Content-Type":"application/json"
    },
    body: JSON.stringify({model:MODEL_MAIN, max_tokens:max, system:sys, messages:[{role:"user",content:usr}]})
  });
  if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e?.error?.message||`HTTP ${r.status}`); }
  const d = await r.json();
  if (d.error) throw new Error(d.error.message);
  const full = d.content?.[0]?.text ?? "";
  onChunk(full);
  return full;
}

function xJSON(raw) {
  if (!raw || !raw.trim()) throw new Error("Réponse vide de l'API.");
  let s = raw.replace(/```json/gi,"").replace(/```/g,"").trim();
  try { return JSON.parse(s); } catch {}
  const a=s.indexOf("{"), b=s.lastIndexOf("}");
  if (a!==-1&&b!==-1) { try { return JSON.parse(s.slice(a,b+1)); } catch {} }
  try {
    let f = a!==-1?s.slice(a):s;
    f = f.replace(/,?\s*"[^"]*$/,"");
    const oc=(f.match(/\{/g)||[]).length-(f.match(/\}/g)||[]).length;
    const ob=(f.match(/\[/g)||[]).length-(f.match(/\]/g)||[]).length;
    f += "]".repeat(Math.max(0,ob))+"}".repeat(Math.max(0,oc));
    return JSON.parse(f);
  } catch {}
  throw new Error(`JSON invalide. Reçu: ${s.slice(0,120)}…`);
}

const ArrowLeft  = ({s=18}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>;
const ArrowRight = ({s=18}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const SunIco     = ({s=16}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>;
const MoonIco    = ({s=16}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
const PlusIco    = ({s=16}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const XicoSvg    = ({s=12}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const WpIco      = ({s=16}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 1.5c2.347 0 4.484.872 6.109 2.302L4.802 17.609A8.473 8.473 0 013.5 12c0-4.687 3.813-8.5 8.5-8.5zm0 17c-2.347 0-4.484-.872-6.109-2.302l13.307-13.807A8.473 8.473 0 0120.5 12c0 4.687-3.813 8.5-8.5 8.5z"/></svg>;
const GscIco     = ({s=18}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/><path d="M8 12a4 4 0 0 1 8 0" stroke="#4285F4" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="16" r="2" fill="#EA4335"/><path d="M6 16h2M16 16h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const TrendUp    = ({s=12}) => <svg width={s} height={s} viewBox="0 0 12 12" fill="none"><polyline points="1,9 4,5 7,7 11,2" stroke="#1DAF61" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const TrendDown  = ({s=12}) => <svg width={s} height={s} viewBox="0 0 12 12" fill="none"><polyline points="1,3 4,7 7,5 11,10" stroke="#FB3737" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const SearchIco  = ({s=14}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const CheckIco   = ({s=16}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

const Logo = ({dark}) => (
  <img src={LogoPowarticle} alt="PowArticle" style={{height:"32px",width:"auto"}} />
);

const Arr = ({dir="right",fill=false,lg=false,disabled=false,onClick}) => (
  <button className={`arr${fill?" fill":""}${lg?" lg":""}`} disabled={disabled} onClick={onClick}>
    {dir==="right"?<ArrowRight s={lg?20:17}/>:<ArrowLeft s={lg?20:17}/>}
  </button>
);

const Ring = ({label,score,color}) => {
  const s = Math.min(100,Math.max(0,Number(score)||0));
  return (
    <div style={{textAlign:"center"}}>
      <div style={{width:68,height:68,borderRadius:"50%",background:"var(--card)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 8px"}}>
        <div className="ring" style={{background:`conic-gradient(${color} ${s*3.6}deg, #e8e8e8 ${s*3.6}deg)`,outline:"none",boxShadow:"none",border:"none"}}>
          <div className="ring-in" style={{color}}>{s}</div>
        </div>
      </div>
      <div style={{fontSize:11,color:"var(--text3)",fontFamily:"var(--f-body)"}}>{label}</div>
    </div>
  );
};

const SeoBar = ({value,min=0,ok1,ok2,max,label}) => {
  const pct = Math.min(100,(value/max)*100);
  const ok1Pct=(ok1/max)*100, ok2Pct=(ok2/max)*100;
  const isGreen=value>=ok1&&value<=ok2;
  const isOrange=(value>=min&&value<ok1)||(value>ok2&&value<=max*1.15);
  const color=isGreen?"#1DAF61":isOrange?"#FA600A":"#FB3737";
  const status=isGreen?"Bon":value<ok1?"Trop court":"Trop long";
  return (
    <div style={{marginTop:8}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
        <span style={{fontSize:11,fontWeight:600,color:"var(--text3)"}}>{label}</span>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:11,fontWeight:700,color}}>{status}</span>
          <span style={{fontSize:11,color:"var(--text3)"}}>{value} car.</span>
        </div>
      </div>
      <div className="seo-bar-track">
        <div className="seo-zone" style={{left:`${ok1Pct}%`,width:`${ok2Pct-ok1Pct}%`,background:"#1DAF61"}}/>
        <div className="seo-bar-fill" style={{width:`${Math.min(pct,100)}%`,background:color}}/>
        <div style={{position:"absolute",top:-3,left:`${ok1Pct}%`,width:2,height:14,background:"#1DAF61",opacity:.5,borderRadius:1}}/>
        <div style={{position:"absolute",top:-3,left:`${ok2Pct}%`,width:2,height:14,background:"#1DAF61",opacity:.5,borderRadius:1}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
        <span style={{fontSize:10,color:"var(--text3)"}}>{min}</span>
        <span style={{fontSize:10,color:"#1DAF61",fontWeight:600}}>{ok1}–{ok2} ✓</span>
        <span style={{fontSize:10,color:"var(--text3)"}}>{max}</span>
      </div>
    </div>
  );
};

const SeoCheckItem = ({ok,warn,label,detail}) => {
  const color=ok?"#1DAF61":warn?"#FA600A":"#FB3737";
  return (
    <div className="seo-check-item">
      <div className="seo-bullet" style={{background:color,marginTop:3,flexShrink:0}}/>
      <div>
        <span style={{fontWeight:500,color:"var(--text)"}}>{label}</span>
        {detail&&<span style={{color:"var(--text3)",marginLeft:6}}>— {detail}</span>}
      </div>
    </div>
  );
};

function buildSeoChecks(d) {
  const kw=(d.keyword||"").toLowerCase().trim();
  const title=(d.selectedTitle||"").toLowerCase();
  const meta=(d.metaDescription||"").toLowerCase();
  const slug=(d.slug||"").toLowerCase();
  const html=(d.article||"");
  const plain=html.replace(/<[^>]+>/g,"").toLowerCase();
  const intro=plain.slice(0,Math.ceil(plain.length*0.15));
  const kwCount=kw?(plain.match(new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g,"\\$&"),"gi"))||[]).length:0;
  const wordCount=plain.split(/\s+/).filter(Boolean).length;
  const density=wordCount>0?((kwCount/wordCount)*100).toFixed(1):0;
  const h2matches=(html.match(/<h[23][^>]*>([^<]+)<\/h[23]>/gi)||[]).map(h=>h.replace(/<[^>]+>/g,"").toLowerCase());
  const kwInH2=h2matches.some(h=>kw&&h.includes(kw));
  const titleLen=(d.selectedTitle||"").length;
  const metaLen=(d.metaDescription||"").length;
  return [
    {ok:kw&&title.includes(kw),warn:false,label:"Keyphrase dans le titre",detail:kw&&title.includes(kw)?"✓ présent":"absent"},
    {ok:titleLen>=50&&titleLen<=60,warn:titleLen>=30&&titleLen<50||titleLen>60&&titleLen<=70,label:"Longueur du titre",detail:`${titleLen} car. (idéal 50–60)`},
    {ok:kw&&meta.includes(kw),warn:false,label:"Keyphrase dans la méta-description",detail:kw&&meta.includes(kw)?"✓ présent":"absent"},
    {ok:metaLen>=120&&metaLen<=156,warn:metaLen>=70&&metaLen<120||metaLen>156&&metaLen<=175,label:"Longueur méta-description",detail:`${metaLen} car. (idéal 120–156)`},
    {ok:kw&&slug.includes(kw.replace(/\s+/g,"-")),warn:kw&&slug.includes(kw.split(" ")[0]),label:"Keyphrase dans le slug",detail:slug||"non défini"},
    {ok:kw&&intro.includes(kw),warn:false,label:"Keyphrase dans l'introduction",detail:kw&&intro.includes(kw)?"✓ présent":"absent"},
    {ok:kwInH2,warn:false,label:"Keyphrase dans un H2/H3",detail:kwInH2?"✓ présent":"absent"},
    {ok:density>=0.5&&density<=3,warn:density>3&&density<=4.5,label:"Densité keyphrase",detail:`${density}% (idéal 0.5–3%)`},
    {ok:wordCount>=900,warn:wordCount>=300,label:"Longueur du contenu",detail:`~${wordCount} mots`},
    {ok:(html.match(/<a\s/gi)||[]).length>=1,warn:false,label:"Liens dans l'article",detail:`${(html.match(/<a\s/gi)||[]).length} lien(s)`},
  ];
}

const SerpPreview = ({title,meta,slug,site="votre-site.com"}) => {
  const tc=(title||"").length>=50&&(title||"").length<=60?"#1DAF61":(title||"").length>=30?"#FA600A":"#FB3737";
  const mc=(meta||"").length>=120&&(meta||"").length<=156?"#1DAF61":(meta||"").length>=70?"#FA600A":"#FB3737";
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <span style={{fontSize:10,fontWeight:700,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.1em"}}>Aperçu Google</span>
        <span style={{fontSize:10,padding:"2px 8px",borderRadius:999,background:`${tc}18`,color:tc,fontWeight:600}}>Titre {(title||"").length} car.</span>
        <span style={{fontSize:10,padding:"2px 8px",borderRadius:999,background:`${mc}18`,color:mc,fontWeight:600}}>Méta {(meta||"").length} car.</span>
      </div>
      <div className="serp-preview">
        <div className="serp-url">https://{site}/{slug||"votre-article"}/</div>
        <div className="serp-title">{(title||"").length>60?title.slice(0,60)+"...":title||"Titre de l'article"}</div>
        <div className="serp-desc">{(meta||"").length>156?meta.slice(0,156)+"...":meta||"Méta-description..."}</div>
      </div>
    </div>
  );
};

const SeoOverall = ({checks}) => {
  const green=checks.filter(c=>c.ok).length;
  const orange=checks.filter(c=>!c.ok&&c.warn).length;
  const score=Math.round((green/checks.length)*100);
  const cls=score>=70?"good":score>=40?"ok":"bad";
  const emoji=score>=70?"🟢":score>=40?"🟠":"🔴";
  return (
    <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <div className={`seo-overall ${cls}`}>{emoji} Analyse SEO — {score>=70?"Bon":score>=40?"À améliorer":"Insuffisant"} ({score}/100)</div>
      <span style={{fontSize:11,color:"var(--text3)"}}>{green} vert · {orange} orange · {checks.length-green-orange} rouge</span>
    </div>
  );
};

const PageHeader = ({step,title,accent,onBack,backLabel="Retour",right}) => (
  <div className="up" style={{marginBottom:36}}>
    {onBack&&(<button className="back-btn" onClick={onBack} style={{marginBottom:22}}><Arr dir="left"/> {backLabel}</button>)}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:14}}>
      <div>
        {step&&<p style={{fontSize:10,fontWeight:700,color:"var(--orange)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:10,fontFamily:"var(--f-body)"}}>{step}</p>}
        <h2 style={{fontFamily:"var(--f-display)",fontSize:"clamp(22px,4vw,30px)",fontWeight:700,letterSpacing:"-0.025em",color:"var(--text)",lineHeight:1.1}}>
          {title}{accent&&<><br/><span style={{color:"var(--orange)"}}>{accent}</span></>}
        </h2>
      </div>
      {right}
    </div>
  </div>
);

const WpModal = ({article,onClose}) => {
  const [wpUrl,setWpUrl]=useState("");
  const [apiKey,setApiKey]=useState("");
  const [status,setStatus]=useState("idle");
  const [msg,setMsg]=useState("");
  const [postUrl,setPostUrl]=useState("");
  const [editUrl,setEditUrl]=useState("");
  const [pubMode,setPubMode]=useState("draft");

  async function doPublish() {
    if (!wpUrl.trim()||!apiKey.trim()) { setMsg("URL et clé API requis."); setStatus("error"); return; }
    setStatus("loading"); setMsg("");
    try {
      const base=wpUrl.trim().replace(/\/$/,"");
      const res=await fetch(`${base}/wp-json/articlai/v1/publish`,{
        method:"POST",
        headers:{"Content-Type":"application/json","X-ArticlAI-Key":apiKey.trim()},
        body:JSON.stringify({title:article.selectedTitle||article.keyword,content:article.article||"",excerpt:article.metaDescription||"",status:pubMode,tags:article.tags||[]}),
      });
      const data=await res.json().catch(()=>({}));
      if (!res.ok||!data.success) throw new Error(data?.error||`Erreur HTTP ${res.status}`);
      setPostUrl(data.link||""); setEditUrl(data.edit_link||"");
      setStatus("success"); setMsg(pubMode==="publish"?"Article publié !":"Brouillon sauvegardé !");
    } catch(e) {
      setStatus("error");
      setMsg(e.message.includes("Failed to fetch")?"Impossible de contacter le site. Vérifiez l'URL et le plugin.":e.message);
    }
  }

  return (
    <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal" style={{maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:40,height:40,borderRadius:12,background:"rgba(250,96,10,0.1)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--orange)"}}><WpIco s={20}/></div>
            <div>
              <div style={{fontFamily:"var(--f-display)",fontWeight:700,fontSize:16,color:"var(--text)"}}>Publier sur WordPress</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Via le plugin ArticlAI Connector</div>
            </div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text3)",padding:4}}><XicoSvg s={16}/></button>
        </div>
        {status==="success"?(
          <div>
            <div className="success-box" style={{display:"flex",alignItems:"center",gap:10}}><CheckIco s={18}/> {msg}</div>
            {postUrl&&<a href={postUrl} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:8,background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:"11px 16px",textDecoration:"none",color:"var(--green)",fontSize:13,fontWeight:600,marginBottom:10}}>↗ Voir l'article</a>}
            {editUrl&&<a href={editUrl} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:8,background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:"11px 16px",textDecoration:"none",color:"var(--orange)",fontSize:13,fontWeight:600,marginBottom:18}}>✏️ Modifier dans WordPress</a>}
            <button className="ghost-btn" style={{width:"100%",justifyContent:"center"}} onClick={onClose}>Fermer</button>
          </div>
        ):(
          <>
            <div style={{background:"rgba(59,130,246,0.06)",border:"1px solid rgba(59,130,246,0.15)",borderRadius:12,padding:"14px 16px",marginBottom:18}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--blue)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Étape 1 — Installer le plugin</div>
              <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.7}}>
                1. Téléchargez <strong>articlai-connector.zip</strong><br/>
                2. WordPress : <strong>Extensions → Ajouter → Téléverser</strong><br/>
                3. Activez, puis <strong>Réglages → ArticlAI</strong> pour votre clé API
              </div>
            </div>
            {status==="error"&&<div className="err" style={{marginBottom:14}}><span>{msg}</span><span onClick={()=>setStatus("idle")} style={{cursor:"pointer",marginLeft:12,fontWeight:700}}>✕</span></div>}
            <div style={{marginBottom:14}}>
              <label className="lbl">URL du site WordPress</label>
              <input className="inp" placeholder="https://mon-site.com" value={wpUrl} onChange={e=>setWpUrl(e.target.value)}/>
            </div>
            <div style={{marginBottom:14}}>
              <label className="lbl">Clé API ArticlAI</label>
              <input className="inp" type="password" placeholder="aai_xxxxxxxxxxxxxxxx" value={apiKey} onChange={e=>setApiKey(e.target.value)}/>
            </div>
            <div style={{marginBottom:18}}>
              <label className="lbl">Mode de publication</label>
              <div style={{display:"flex",gap:8}}>
                {[{v:"draft",l:"💾 Brouillon"},{v:"publish",l:"🚀 Publier"}].map(m=>(
                  <button key={m.v} className={`len-pill ${pubMode===m.v?"active":""}`} onClick={()=>setPubMode(m.v)}>{m.l}</button>
                ))}
              </div>
            </div>
            <div style={{background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:"12px 16px",marginBottom:20}}>
              <div style={{fontSize:10,fontWeight:700,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Article</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{article.selectedTitle||article.keyword}</div>
            </div>
            <button className="cta-btn green" style={{width:"100%",justifyContent:"center"}} onClick={doPublish} disabled={status==="loading"}>
              {status==="loading"?"En cours…":pubMode==="publish"?"Publier":"Envoyer en brouillon"}
              {status!=="loading"&&<span className="cta-circle"><WpIco s={16}/></span>}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

function makeTrend(base,len=28,variance=0.35,upward=true) {
  const pts=[]; let v=base*(0.5+Math.random()*0.3);
  for (let i=0;i<len;i++) {
    const drift=upward?1+(i/len)*0.6:1-(i/len)*0.2;
    v=Math.max(0,v*drift*(1+(Math.random()-0.48)*variance));
    pts.push(Math.round(v));
  }
  return pts;
}
function mockGsc(title,slug,daysAgo,status) {
  const isNew=daysAgo<7,isMid=daysAgo<21;
  const clicks28=isNew?0:Math.round(Math.random()*(isMid?180:1400)+10);
  const impr28=isNew?0:Math.round(clicks28*(8+Math.random()*18));
  const ctr=impr28>0?((clicks28/impr28)*100).toFixed(1):"—";
  const pos=isNew?"—":(3+Math.random()*22).toFixed(1);
  const up=Math.random()>0.3;
  return {title,slug,daysAgo,status,clicks28,impr28,ctr,pos,
    trendClicks:isNew?[]:makeTrend(clicks28/28,28,0.5,up),
    trendImpr:isNew?[]:makeTrend(impr28/28,28,0.4,up),up};
}
const MOCK_ARTICLES=[
  mockGsc("Meilleur outil SEO en 2026 : comparatif complet","meilleur-outil-seo-2026",45,"indexed"),
  mockGsc("Stratégie de contenu : guide pour PME","strategie-contenu-pme",30,"indexed"),
  mockGsc("Comment optimiser ses balises meta","optimiser-balises-meta",21,"indexed"),
  mockGsc("Audit SEO technique : 12 points essentiels","audit-seo-technique",14,"pending"),
  mockGsc("Rédiger un article blog qui se positionne","rediger-article-blog-seo",8,"pending"),
  mockGsc("Maillage interne : stratégie et pratiques","maillage-interne-seo",3,"waiting"),
];
const MOCK_GLOBAL={
  clicks:MOCK_ARTICLES.reduce((s,a)=>s+a.clicks28,0),
  impr:MOCK_ARTICLES.reduce((s,a)=>s+a.impr28,0),
  avgPos:(MOCK_ARTICLES.filter(a=>a.pos!=="—").reduce((s,a)=>s+parseFloat(a.pos),0)/MOCK_ARTICLES.filter(a=>a.pos!=="—").length).toFixed(1),
  indexed:MOCK_ARTICLES.filter(a=>a.status==="indexed").length,
  trendClicks:makeTrend(420,28,0.4,true),
  trendImpr:makeTrend(5200,28,0.35,true),
};

const Sparkline = ({data=[],color="#FA600A",w=120,h=36,fill=true}) => {
  if (!data||data.length<2) return <div style={{width:w,height:h,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontSize:10,color:"#999"}}>—</span></div>;
  const mn=Math.min(...data),mx=Math.max(...data),range=mx-mn||1,pad=3;
  const pts=data.map((v,i)=>[pad+(i/(data.length-1))*(w-pad*2),pad+(1-(v-mn)/range)*(h-pad*2)]);
  const line=pts.map((p,i)=>`${i===0?"M":"L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area=line+` L${pts[pts.length-1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{overflow:"visible"}}>
      {fill&&<path d={area} fill={color} opacity="0.12"/>}
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="2.5" fill={color}/>
    </svg>
  );
};

export default function App() {
  const [dark,setDark]=useState(false);
  const [page,setPage]=useState("ideas-form");
  const [busy,setBusy]=useState(false);
  const [step,setStep]=useState("");
  const [err,setErr]=useState("");
  const [iForm,setIForm]=useState({activity:"",website:"",keywords:"",country:"France",language:"Français"});
  const [ideas,setIdeas]=useState([]);
  const [form,setForm]=useState({keyword:"",niche:"",audience:"",tone:"Expert",length:"moyen"});
  const [slugInp,setSlugInp]=useState("");
  const [links,setLinks]=useState([]); const [lInp,setLInp]=useState("");
  const [tags,setTags]=useState([]); const [tInp,setTInp]=useState("");
  const [result,setResult]=useState(null);
  const [hist,setHist]=useState([]);
  const [selH,setSelH]=useState(null);
  const [copied,setCopied]=useState("");
  const [showWp,setShowWp]=useState(false);
  const [ideaTitle, setIdeaTitle] = useState("");
  const [scConn,setScConn]=useState(false);
  const [scRange,setScRange]=useState("28");
  const [scSort,setScSort]=useState("clicks");
  const [scSearch,setScSearch]=useState("");
  const [streaming,setStreaming]=useState(false);
  const [streamHtml,setStreamHtml]=useState("");
  const [userEmail,setUserEmail]=useState("demo@gmail.com");
  const [scProperties,setScProperties]=useState([]);
  const [scData,setScData]=useState(null);
  const [scTotals,setScTotals]=useState(null);
  const [selectedProperty,setSelectedProperty]=useState("");

  async function fetchSearchConsoleData(siteUrl) {
    try {
      const requestBody = {
        site_url: siteUrl,
        start_date: getDateRange(scRange).start,
        end_date: getDateRange(scRange).end,
        dimensions: ['page']
      };
      
      // Fetch detailed page data
      const response = await fetch('/api/search-console.php', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(requestBody)
      });
      
      // Fetch accurate totals (no dimensions)
      const totalsResponse = await fetch('/api/search-console-totals.php', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          site_url: siteUrl,
          start_date: getDateRange(scRange).start,
          end_date: getDateRange(scRange).end
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setScData(data);
        
        if (totalsResponse.ok) {
          const totalsData = await totalsResponse.json();
          setScTotals(totalsData);
        }
        
        return data;
      } else {
        console.error('Failed to fetch Search Console data:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error fetching Search Console data:', error);
      return null;
    }
  }

  function getDateRange(days) {
    const today = new Date();
    
    const end = new Date();
    end.setDate(today.getDate() - 2); // Day before yesterday (2 days ago)
    
    const start = new Date();
    start.setDate(today.getDate() - parseInt(days) - 1); // X days ago + 1 to account for the 2-day delay
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }

  function getDisplayData() {
    if (scTotals && scTotals.rows && scTotals.rows.length > 0) {
      // Use accurate totals from API (no dimensions = exact totals)
      const totalsRow = scTotals.rows[0];
      const pageCount = scData && scData.rows ? scData.rows.length : 0;
      
      return {
        clicks: totalsRow.clicks || 0,
        impressions: totalsRow.impressions || 0,
        avgPos: totalsRow.position ? totalsRow.position.toFixed(1) : "—",
        indexed: pageCount, // Number of pages found
        articles: scData && scData.rows ? scData.rows : []
      };
    } else if (scData && scData.rows && scData.rows.length > 0) {
      // Fallback to calculated totals from individual rows
      const totalClicks = scData.rows.reduce((sum, row) => sum + (row.clicks || 0), 0);
      const totalImpressions = scData.rows.reduce((sum, row) => sum + (row.impressions || 0), 0);
      const avgPosition = totalImpressions > 0 
        ? (scData.rows.reduce((sum, row) => sum + ((row.position || 0) * (row.impressions || 0)), 0) / totalImpressions).toFixed(1)
        : "—";
      const indexedCount = scData.rows.length;

      return {
        clicks: totalClicks,
        impressions: totalImpressions,
        avgPos: avgPosition,
        indexed: indexedCount,
        articles: scData.rows
      };
    }
    
    // Fallback to mock data
    return {
      clicks: MOCK_GLOBAL.clicks,
      impressions: MOCK_GLOBAL.impr,
      avgPos: MOCK_GLOBAL.avgPos,
      indexed: MOCK_GLOBAL.indexed,
      articles: MOCK_ARTICLES
    };
  }

  async function checkAuthStatus() {
    try {
      const response = await fetch('/api/get-user.php');
      const userData = await response.json();
      if (userData.authenticated) {
        setScConn(true);
        setUserEmail(userData.email);
        // Fetch available properties
        const propsResponse = await fetch('/api/get-properties.php');
        const propsData = await propsResponse.json();
        if (propsData.siteEntry && propsData.siteEntry.length > 0) {
          setScProperties(propsData.siteEntry);
          // Auto-select first property and fetch its data
          const firstProperty = propsData.siteEntry[0].siteUrl;
          setSelectedProperty(firstProperty);
          await fetchSearchConsoleData(firstProperty);
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  }

  useEffect(()=>{
    (async()=>{ try { const r=await window.storage.get(STORE); if(r) setHist(JSON.parse(r.value)); } catch {} })();
    
    // Check for Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const googleAuth = urlParams.get('google_auth');
    if (googleAuth === 'success') {
      // Check actual authentication status
      checkAuthStatus();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (googleAuth === 'error') {
      const reason = urlParams.get('reason');
      console.error('Google authentication failed:', reason);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Check if user is already authenticated
      checkAuthStatus();
    }
  },[]);

  // Refetch data when range changes
  useEffect(() => {
    if (scConn && selectedProperty && userEmail !== "demo@gmail.com") {
      fetchSearchConsoleData(selectedProperty);
    }
  }, [scRange]);

  async function saveHist(a) {
    const nh=[a,...hist].slice(0,20); setHist(nh);
    try { await window.storage.set(STORE,JSON.stringify(nh)); } catch {}
  }

  function copy(text,id) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(()=>{ setCopied(id); setTimeout(()=>setCopied(""),2200); })
        .catch(()=>{ setCopyModal({text,id}); setCopied(id); setTimeout(()=>setCopied(""),4000); });
    } else {
      setCopyModal({text,id}); setCopied(id); setTimeout(()=>setCopied(""),4000);
    }
  }
  const [copyModal,setCopyModal]=useState(null);
  function CopyModal() {
    if (!copyModal) return null;
    return (
      <div className="modal-overlay" onClick={()=>setCopyModal(null)}>
        <div className="modal" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontFamily:"var(--f-display)",fontWeight:700,fontSize:15,color:"var(--text)"}}>Copier le contenu</div>
            <button onClick={()=>setCopyModal(null)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text3)"}}><XicoSvg s={16}/></button>
          </div>
          <div style={{fontSize:12,color:"var(--text3)",marginBottom:10}}>Le texte est sélectionné — faites <kbd style={{background:"var(--border)",padding:"2px 6px",borderRadius:4,fontSize:11}}>Ctrl+C</kbd> ou <kbd style={{background:"var(--border)",padding:"2px 6px",borderRadius:4,fontSize:11}}>⌘+C</kbd> pour copier.</div>
          <textarea readOnly value={copyModal.text}
            style={{width:"100%",height:180,background:"var(--input-bg)",border:"1.5px solid var(--orange)",borderRadius:12,padding:12,fontSize:12,fontFamily:"var(--f-mono)",color:"var(--text)",resize:"vertical",outline:"none"}}
            onFocus={e=>e.target.select()}
            ref={el=>{if(el){el.focus();el.select();}}}
          />
        </div>
      </div>
    );
  }
  const CopyBtn = ({text,id,label="Copier"}) => (
    <button className={`copy-btn ${copied===id?"done":""}`} onClick={()=>copy(text,id)}>
      {copied===id?"✓ Copié":label}
    </button>
  );

  async function doIdeas() {
    if (!iForm.activity.trim()&&!iForm.keywords.trim()) return;
    setErr(""); setBusy(true); setStep("Analyse de votre secteur…");
    try {
      const sys=`Tu es un expert SEO senior. Nous sommes en ${NOW_DATE}. Réponds UNIQUEMENT avec un objet JSON valide, sans markdown.`;
      const usr=`Génère 10 idées d'articles SEO à fort potentiel pour ${NOW_YEAR}.
Activité : ${iForm.activity||"non précisé"} | Site : ${iForm.website||"—"} | Mots-clés : ${iForm.keywords||"—"} | Pays : ${iForm.country} | Langue : ${iForm.language}
Varie les intentions (informationnelle, commerciale, transactionnelle, navigationnelle) et difficultés (Facile/Moyen/Difficile).
{"ideas":[{"title":"…","keyword":"…","difficulty":"Facile","intent":"Informationnelle","why":"raison stratégique en 1 phrase"},{"title":"…","keyword":"…","difficulty":"Moyen","intent":"Commerciale","why":"…"},{"title":"…","keyword":"…","difficulty":"Difficile","intent":"Transactionnelle","why":"…"},{"title":"…","keyword":"…","difficulty":"Facile","intent":"Navigationnelle","why":"…"},{"title":"…","keyword":"…","difficulty":"Moyen","intent":"Informationnelle","why":"…"},{"title":"…","keyword":"…","difficulty":"Facile","intent":"Commerciale","why":"…"},{"title":"…","keyword":"…","difficulty":"Moyen","intent":"Transactionnelle","why":"…"},{"title":"…","keyword":"…","difficulty":"Difficile","intent":"Informationnelle","why":"…"},{"title":"…","keyword":"…","difficulty":"Facile","intent":"Commerciale","why":"…"},{"title":"…","keyword":"…","difficulty":"Moyen","intent":"Navigationnelle","why":"…"}]}`;
      const raw = await callClaude(usr, sys, 2500, MODEL_FAST, (partial) => {
        setStep(`Analyse en cours… (${partial.length} caractères)`);
      });
      const d=xJSON(raw);
      if (!Array.isArray(d.ideas)||!d.ideas.length) throw new Error("Tableau vide");
      setIdeas(d.ideas); setPage("ideas-result");
    } catch(e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  function toSlug(str) {
    return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-z0-9\s-]/g,"").trim().replace(/\s+/g,"-").slice(0,60);
  }

  async function doArticle() {
    if (!form.keyword.trim()) return;
    setErr(""); setBusy(true); setResult(null);
    const wc=form.length==="court"?400:form.length==="moyen"?700:1000;
    const userSlug=slugInp.trim()?toSlug(slugInp):"";
    const linksStr=links.length?`Liens internes : ${links.join(", ")}`:"";
    const titleCtx=ideaTitle?`Titre exact de l'article : "${ideaTitle}". `:"";
    const nicheCtx=form.niche?`Secteur : "${form.niche}". `:"";
    const sysMeta=`Tu es expert SEO. Réponds UNIQUEMENT avec un objet JSON valide, sans markdown, sans texte avant ou après.`;
    const sysArt=`Tu es rédacteur web SEO expert. Réponds UNIQUEMENT avec du HTML brut, sans markdown, sans explications, sans backticks.`;

    try {
    const promptMeta=`Génère les métadonnées SEO. ${titleCtx}Mot-clé cible : "${form.keyword}". ${nicheCtx}Langue: ${iForm.language} | Pays: ${iForm.country} | Slug: ${userSlug||"kebab-case auto"}
    IMPORTANT : les titres doivent être grammaticalement corrects et naturels en ${iForm.language}. Ne pas répéter le mot-clé tel quel si c'est une suite de mots sans syntaxe — reformule-le en titre accrocheur.
    Retourne UNIQUEMENT ce JSON:
    {"titles":["titre naturel 1","titre naturel 2","titre naturel 3","titre naturel 4","titre naturel 5"],"selectedTitle":"meilleur titre 50-60 car","metaDescription":"méta 120-156 car naturelle avec le mot-clé","slug":"le-slug","keywords":["kw1","kw2","kw3","kw4","kw5"],"seoScore":82,"readabilityScore":76,"keywordDensity":1.5,"imagePrompt":"description visuelle","unsplashQueries":["q1","q2","q3"],"schema":{"@context":"https://schema.org","@type":"Article","headline":"titre","description":"desc","datePublished":"2026-03-06"}}`;

    const promptArt=`Tu es expert SEO senior avec 10+ années d'expérience. Rédige un article RÉFÉRENCE optimisé Google ET moteurs IA.
    Sujet : "${ideaTitle||form.keyword}"
    Mot-clé : "${form.keyword}"
    Pays : ${iForm.country} | Ton : ${form.tone}
    Longueur : ${wc} mots minimum

    ⚠️ RÈGLES STRICTES (NON-NÉGOCIABLES) :

    1. AUCUNE affirmation sans source vérifiable
      ❌ "85% des requêtes par IA" (FAKE)
      ✅ "Selon Semrush 2024, X% des..." OU sans chiffre si non vérifié

    2. Ajouter 2-3 TABLEAUX citables
      Format tableau :
      | KPI | Définition | Benchmark | Outil |
      |-----|-----------|-----------|------|
      Exemple : Taux de citation | % de réponses IA vous citant | 5-20% | Peec.ia |

    3. Ajouter 1 FRAMEWORK CLAIR et citables
      Exemple :
      Les 5 KPIs GEO essentiels :
      1. Taux de citation dans les réponses IA
      2. Part de voix vs concurrents
      3. Sentiment des mentions
      4. Position dans la réponse
      5. Trafic depuis moteurs IA

    4. Ajouter 3-5 EXEMPLES CONCRETS vérifiables
      ✅ "Testé sur 100 requêtes marketing français..."
      ✅ "Dans un audit de 50 sites e-commerce..."
      ✅ Cas réels ou méthodologie testée

    5. CITER LES SOURCES explicitement
      ✅ "Selon BrightEdge" + lien si possible
      ✅ "D'après une étude Moz 2024"
      ✅ "Nos tests sur ChatGPT, Perplexity, Google SGE montrent..."

    6. TON PERSONNEL + RETOURS TERRAIN
      ❌ Générique : "Mesurer performance est important"
      ✅ Personnel : "Dans mes audits GEO, j'ai observé que 70% des marques..."
      ✅ Anecdotes : "Un client B2B français a augmenté ses citations de 30% en..."

    7. STRUCTURES ULTRA-CITABLES PAR LES IA
      ✅ Listes numérotées
      ✅ Définitions en gras
      ✅ Checklists
      ✅ "Les N points essentiels"
      ✅ Tableaux
      ❌ Paragraphes longs sans structures

    STRUCTURE OBLIGATOIRE :
    <h1> Titre accrocheur avec mot-clé
    <p> Intro (2-3 phrases) + contexte
    <h2> Section 1 + <h3> sous-sections
      └─ avec TABLEAU ou LISTE numérotée
    <h2> Section 2 + <h3> sous-sections
      └─ avec EXEMPLE concret vérifiable
    <h2> Section 3 + <h3> sous-sections
      └─ avec FRAMEWORK citables
    <h2> Section 4 + <h3> sous-sections
      └─ avec TABLEAU ou BENCHMARK
    <h2>FAQ</h2>
      <h3>Question 1 ?</h3><p>Réponse sourçée...</p>
      <h3>Question 2 ?</h3><p>Réponse sourçée...</p>
      <h3>Question 3 ?</h3><p>Réponse sourçée...</p>
      <h3>Question 4 ?</h3><p>Réponse sourçée...</p>
      <h3>Question 5 ?</h3><p>Réponse sourçée...</p>
    <h2>Conclusion</h2><p>Résumé + call-to-action</p>

    CRITÈRES DE QUALITÉ EXPERT :
    ✅ Chiffres tracés (source entre parenthèses)
    ✅ Au moins 1 tableau avec données
    ✅ Au moins 1 framework clair et numéroté
    ✅ Au moins 3 exemples réels ou cas d'usage
    ✅ Sources officielles citées (outils, études)
    ✅ Ton expert avec anecdotes personelles
    ✅ AUCUN blabla - chaque phrase = valeur
    ✅ Densité keyphrase naturelle 0.5-2%
    ✅ Phrases courts, citables par IA
    ✅ Pas d'affirmations douteuses

    Format : HTML pur UNIQUEMENT, rien d'autre.`;

      // ÉTAPE 2 : appels parallèles (métadonnées via Haiku + article streaming via Opus)
      setStep("Génération en cours…");

      const [rawMeta, articleHtml] = await Promise.all([
        callClaude(promptMeta, sysMeta, 1500, "claude-haiku-4-5"),
        callClaude(promptArt, sysArt, 6000, MODEL_MAIN, (partial) => {
          setStep(`Rédaction de l'article… (${partial.length} caractères générés)`);
        }),
      ]);

      const meta = xJSON(rawMeta);
      if (userSlug) meta.slug = userSlug;
      if (ideaTitle) meta.selectedTitle = ideaTitle;

      const finalArt = {
        ...meta,
        article: articleHtml,
        keyword:form.keyword, ideaTitle, niche:form.niche, audience:form.audience,
        tone:form.tone, length:form.length, country:iForm.country,
        language:iForm.language, links:[...links], tags:[...tags],
        date:new Date().toLocaleDateString("fr-FR")
      };
      setResult(finalArt);
      await saveHist(finalArt);
      setPage("generate-result");
    } catch(e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const isOnIdeas=page==="ideas-form"||page==="ideas-result";
  const isOnGenerate=page==="generate-form"||page==="generate-result";
  const isOnHistory=page==="history";
  const isOnPerf=page==="performance";

  function Nav() {
    return (
      <nav className="nav">
        <Logo dark={dark}/>
        <div className="step-bar">
          <span style={{color:isOnIdeas?"var(--orange)":"var(--text3)",fontWeight:isOnIdeas?600:400,cursor:"pointer",fontFamily:"var(--f-body)"}} onClick={()=>setPage("ideas-form")}>Idées</span>
          <div className={`step-dot ${isOnIdeas?"active":""}`}/>
          <span style={{color:"var(--border)"}}>—</span>
          <div className={`step-dot ${isOnGenerate?"active":""}`}/>
          <span style={{color:isOnGenerate?"var(--orange)":"var(--text3)",fontWeight:isOnGenerate?600:400,cursor:(ideas.length||result)?"pointer":"default",fontFamily:"var(--f-body)"}} onClick={()=>{if(ideas.length||result)setPage("generate-form");}}>Rédaction</span>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={()=>setPage("performance")} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"var(--f-body)",fontSize:13,fontWeight:500,color:isOnPerf?"var(--orange)":"var(--text3)",transition:"color 0.2s",display:"flex",alignItems:"center",gap:6}}>
            <GscIco s={14}/> Performance{scConn&&<span style={{width:6,height:6,borderRadius:"50%",background:"#1DAF61",display:"inline-block",marginLeft:2}}/>}
          </button>
          <button onClick={()=>setPage("history")} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"var(--f-body)",fontSize:13,fontWeight:500,color:isOnHistory?"var(--orange)":"var(--text3)",transition:"color 0.2s"}}>
            Historique{hist.length>0?` (${hist.length})`:""}
          </button>
          <div className="theme-pill">
            <button className={`theme-btn ${!dark?"active":""}`} onClick={()=>setDark(false)}><SunIco s={15}/></button>
            <button className={`theme-btn ${dark?"active":""}`} onClick={()=>setDark(true)}><MoonIco s={15}/></button>
          </div>
        </div>
      </nav>
    );
  }

  function ErrBox() {
    if (!err) return null;
    return <div className="err"><span>{err}</span><span onClick={()=>setErr("")} style={{cursor:"pointer",marginLeft:12,fontWeight:700}}>✕</span></div>;
  }

  if (busy) return (
    <div className={`app ${dark?"dm":"lm"}`}><GlobalStyles/>
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh"}}>
        <div style={{width:220,marginBottom:40}}>
          {[0,1,2].map(i=>(
            <div key={i} style={{height:2,borderRadius:999,background:"var(--border)",marginBottom:i<2?10:0,overflow:"hidden"}}>
              <div className="loader-bar" style={{animationDelay:`${i*0.28}s`}}/>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center",maxWidth:280}}>
          <div style={{fontFamily:"var(--f-display)",fontWeight:600,fontSize:17,color:"var(--text)",marginBottom:12,letterSpacing:"-0.02em",lineHeight:1.3}}>{step}</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            {[0,1,2].map(i=>(<div key={i} className="loader-dot" style={{animationDelay:`${i*0.22}s`}}/>))}
          </div>
        </div>
      </div>
    </div>
  );

  if (page==="ideas-form") return (
    <div className={`app ${dark?"dm":"lm"}`}><GlobalStyles/><Nav/>
      <div className="wrap">
        <div className="up" style={{textAlign:"center",marginBottom:52}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(250,96,10,0.1)",border:"1px solid rgba(250,96,10,0.18)",borderRadius:"999px",padding:"6px 16px",marginBottom:24}}>
            <div style={{width:5,height:5,borderRadius:"50%",background:"var(--orange)"}}/>
            <span style={{fontSize:11,fontWeight:700,color:"var(--orange)",letterSpacing:"0.1em",textTransform:"uppercase",fontFamily:"var(--f-body)"}}>Étape 01 — Stratégie</span>
          </div>
          <h1 style={{fontFamily:"var(--f-display)",fontSize:"clamp(34px,5vw,52px)",fontWeight:700,lineHeight:1.08,letterSpacing:"-0.03em",color:"var(--text)",marginBottom:16}}>
            Découvrez vos<br/><span style={{color:"var(--orange)"}}>meilleures opportunités</span>
          </h1>
          <p style={{fontSize:15,color:"var(--text3)",maxWidth:420,margin:"0 auto",lineHeight:1.65}}>Décrivez votre activité et obtenez 10 idées d'articles stratégiques, prêtes à publier.</p>
        </div>
        <div className="card u1">
          <ErrBox/>
          <div style={{marginBottom:18}}>
            <label className="lbl">Activité / Secteur *</label>
            <input className="inp" placeholder="Ex : agence web, e-commerce, cabinet RH…" value={iForm.activity} onChange={e=>setIForm({...iForm,activity:e.target.value})} onKeyDown={e=>e.key==="Enter"&&doIdeas()}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18}}>
            <div>
              <label className="lbl">Site web</label>
              <input className="inp" placeholder="https://votre-site.com" value={iForm.website} onChange={e=>setIForm({...iForm,website:e.target.value})}/>
            </div>
            <div>
              <label className="lbl">Mots-clés de départ</label>
              <input className="inp" placeholder="SEO, référencement…" value={iForm.keywords} onChange={e=>setIForm({...iForm,keywords:e.target.value})}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:26}}>
            <div>
              <label className="lbl">Pays cible</label>
              <select className="inp" style={{cursor:"pointer"}} value={iForm.country} onChange={e=>setIForm({...iForm,country:e.target.value})}>
                {COUNTRIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl">Langue de rédaction</label>
              <select className="inp" style={{cursor:"pointer"}} value={iForm.language} onChange={e=>setIForm({...iForm,language:e.target.value})}>
                {LANGUAGES.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <button className="cta-btn" onClick={doIdeas} disabled={!iForm.activity.trim()&&!iForm.keywords.trim()}>
              Générer les idées <span className="cta-circle"><ArrowRight s={16}/></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (page==="ideas-result") return (
    <div className={`app ${dark?"dm":"lm"}`}><GlobalStyles/><Nav/>
      <div className="wrap">
        <PageHeader step="Étape 01 — Résultats" title="Vos opportunités" accent="éditoriales"
          onBack={()=>setPage("ideas-form")} backLabel="Modifier la recherche"
          right={<div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span className="tag tag-b">{iForm.country}</span>
            <span className="tag tag-n">{iForm.language}</span>
            <button className="ghost-btn" onClick={()=>{setIdeas([]);setPage("ideas-form");}}><ArrowLeft s={14}/> Nouvelle recherche</button>
          </div>}
        />
        {ideas.map((idea,i)=>(
          <div className="idea-c" key={i}>
            <div style={{width:36,height:36,borderRadius:"50%",background:"rgba(250,96,10,0.1)",color:"var(--orange)",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:"var(--f-display)"}}>{String(i+1).padStart(2,"0")}</div>
            <div style={{flex:1}}>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                <span className="tag" style={{background:`${DIFF_C[idea.difficulty]||"#888"}18`,color:DIFF_C[idea.difficulty]||"#888",border:`1px solid ${DIFF_C[idea.difficulty]||"#888"}28`}}>{idea.difficulty}</span>
                <span className="tag" style={{background:`${INTENT_C[idea.intent]||"#888"}15`,color:INTENT_C[idea.intent]||"#888",border:`1px solid ${INTENT_C[idea.intent]||"#888"}22`}}>{idea.intent}</span>
              </div>
              <div style={{fontFamily:"var(--f-display)",fontWeight:600,fontSize:15,color:"var(--text)",marginBottom:6,letterSpacing:"-0.015em",lineHeight:1.35}}>{idea.title}</div>
              <div style={{fontSize:12,color:"var(--text3)",marginBottom:10}}>Mot-clé : <span style={{color:"var(--orange)",fontWeight:600}}>{idea.keyword}</span></div>
              <div style={{fontSize:13,color:"var(--text2)",background:"var(--input-bg)",borderRadius:10,padding:"9px 14px",borderLeft:"2px solid var(--orange)",lineHeight:1.55}}>{idea.why}</div>
            </div>
            <Arr dir="right" fill onClick={()=>{setForm({keyword:idea.keyword,niche:iForm.activity,audience:"",tone:"Expert",length:"moyen"});setIdeaTitle(idea.title);setSlugInp("");setLinks([]);setTags([]);setResult(null);setPage("generate-form");}}/>
          </div>
        ))}
      </div>
    </div>
  );

  if (page==="generate-form") return (
    <div className={`app ${dark?"dm":"lm"}`}><GlobalStyles/><Nav/>
      <div className="wrap">
        <PageHeader step="Étape 02 — Rédaction" title="Configurez" accent="votre article"
          onBack={()=>setPage(ideas.length?"ideas-result":"ideas-form")}
          backLabel={ideas.length?"Retour aux idées":"Retour"}
        />
        <div className="card u1">
          <ErrBox/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18}}>
            <div>
              <label className="lbl">Mot-clé principal *</label>
              <input className="inp" placeholder="Ex : meilleur outil SEO…" value={form.keyword} onChange={e=>{setForm({...form,keyword:e.target.value});setIdeaTitle("");}}
                style={ideaTitle?{background:"rgba(250,96,10,0.04)",borderColor:"rgba(250,96,10,0.3)"}:{}}
              />
              {ideaTitle&&<div style={{fontSize:11,color:"var(--orange)",marginTop:5,fontWeight:500}}>📌 Titre : {ideaTitle}</div>}
            </div>
            <div>
              <label className="lbl">Slug URL</label>
              <div style={{position:"relative"}}>
                <input className="inp" style={{paddingRight:72}} placeholder="généré automatiquement" value={slugInp}
                  onChange={e=>setSlugInp(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-").replace(/-+/g,"-"))}/>
                {!slugInp&&form.keyword&&(
                  <button onClick={()=>setSlugInp(toSlug(form.keyword))}
                    style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"var(--orange)",border:"none",color:"#fff",fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:999,cursor:"pointer"}}>
                    Générer
                  </button>
                )}
                {slugInp&&(<button onClick={()=>setSlugInp("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"var(--text3)",fontSize:11,cursor:"pointer"}}>✕</button>)}
              </div>
              {slugInp&&(<div style={{fontSize:10,color:"var(--text3)",marginTop:4,fontFamily:"var(--f-mono)"}}>/{slugInp.slice(0,60)}/ <span style={{color:slugInp.length<=60?"var(--green)":"var(--red)",fontWeight:700}}>{slugInp.length}/60</span></div>)}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18}}>
            <div>
              <label className="lbl">Niche / Secteur</label>
              <input className="inp" placeholder="marketing, tech, santé…" value={form.niche} onChange={e=>setForm({...form,niche:e.target.value})}/>
            </div>
            <div>
              <label className="lbl">Public cible</label>
              <input className="inp" placeholder="PME, débutants, directeurs…" value={form.audience} onChange={e=>setForm({...form,audience:e.target.value})}/>
            </div>
          </div>
          <div style={{marginBottom:18}}>
            <label className="lbl">Ton éditorial</label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {TONES_ART.map(t=>(<button key={t} className={`tone-pill ${form.tone===t?"active":""}`} onClick={()=>setForm({...form,tone:t})}>{t}</button>))}
            </div>
          </div>
          <div style={{marginBottom:22}}>
            <label className="lbl">Longueur</label>
            <div style={{display:"flex",gap:8}}>
              {LENGTHS.map(l=>(<button key={l.value} className={`len-pill ${form.length===l.value?"active":""}`} onClick={()=>setForm({...form,length:l.value})}><div>{l.label}</div><div style={{fontSize:10,opacity:.7,marginTop:2}}>{l.sub}</div></button>))}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:22}}>
            <div>
              <label className="lbl">Pays cible</label>
              <select className="inp" style={{cursor:"pointer"}} value={iForm.country} onChange={e=>setIForm({...iForm,country:e.target.value})}>
                {COUNTRIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl">Langue</label>
              <select className="inp" style={{cursor:"pointer"}} value={iForm.language} onChange={e=>setIForm({...iForm,language:e.target.value})}>
                {LANGUAGES.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <hr className="div"/>
          <div style={{marginBottom:18}}>
            <label className="lbl">Liens internes</label>
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <input className="inp" placeholder="https://…" value={lInp} onChange={e=>setLInp(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"){const v=lInp.trim();if(v&&!links.includes(v)){setLinks([...links,v]);setLInp("");}}}}/>
              <button className="arr fill" onClick={()=>{const v=lInp.trim();if(v&&!links.includes(v)){setLinks([...links,v]);setLInp("");}}}><PlusIco s={16}/></button>
            </div>
            {links.length===0?<span style={{fontSize:12,color:"var(--text3)"}}>Aucun lien</span>
              :links.map((l,i)=><span key={i} className="tag tag-o">{l.length>30?l.slice(0,28)+"…":l}<span onClick={()=>setLinks(links.filter((_,j)=>j!==i))} style={{cursor:"pointer",marginLeft:4,display:"inline-flex"}}><XicoSvg s={11}/></span></span>)}
          </div>
          <div style={{marginBottom:26}}>
            <label className="lbl">Tags</label>
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <input className="inp" placeholder="SEO, digital…" value={tInp} onChange={e=>setTInp(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"){const v=tInp.trim().replace(/^#/,"");if(v&&!tags.includes(v)){setTags([...tags,v]);setTInp("");}}}}/>
              <button className="arr fill" onClick={()=>{const v=tInp.trim().replace(/^#/,"");if(v&&!tags.includes(v)){setTags([...tags,v]);setTInp("");}}}><PlusIco s={16}/></button>
            </div>
            {tags.length===0?<span style={{fontSize:12,color:"var(--text3)"}}>Aucun tag</span>
              :tags.map((t,i)=><span key={i} className="tag tag-n">#{t}<span onClick={()=>setTags(tags.filter((_,j)=>j!==i))} style={{cursor:"pointer",marginLeft:4,display:"inline-flex"}}><XicoSvg s={11}/></span></span>)}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <button className="cta-btn" onClick={doArticle} disabled={!form.keyword.trim()}>
              Générer l'article <span className="cta-circle"><ArrowRight s={16}/></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (page==="generate-result") {
    const d=selH||result; if(!d) return null;
    const plain=(d.article||"").replace(/<[^>]+>/g,"");
    const schStr=JSON.stringify(d.schema||{},null,2);
    const checks=buildSeoChecks(d);
    return (
      <div className={`app ${dark?"dm":"lm"}`}><GlobalStyles/><Nav/>
        {showWp&&<WpModal article={d} onClose={()=>setShowWp(false)}/>}
        <CopyModal/>
        <div className="wrap">
          <PageHeader step="Article généré" title={d.keyword}
            onBack={()=>{setSelH(null);setPage("generate-form");}} backLabel="Modifier les paramètres"
            right={<div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <button className="ghost-btn" onClick={()=>setShowWp(true)} disabled={streaming}><WpIco s={14}/> WordPress</button>
              <button className="ghost-btn" onClick={()=>{setResult(null);setSelH(null);setLinks([]);setTags([]);setSlugInp("");setStreaming(false);setStreamHtml("");setForm({keyword:"",niche:"",audience:"",tone:"Expert",length:"moyen"});setPage("generate-form");}}>
                <PlusIco s={13}/> Nouvel article
              </button>
            </div>}
          />

          <div className="card u1" style={{marginBottom:14}}>
            <SerpPreview title={d.selectedTitle} meta={d.metaDescription} slug={d.slug}
              site={iForm.website?iForm.website.replace(/https?:\/\//,"").replace(/\/$/,""):"votre-site.com"}/>
          </div>

          <div className="card u1" style={{marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:18}}>
              <SeoOverall checks={checks}/>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {d.country&&<span className="tag tag-b" style={{margin:0}}>{d.country}</span>}
                {d.language&&<span className="tag tag-g" style={{margin:0}}>{d.language}</span>}
                <span style={{fontSize:11,color:"var(--text3)"}}>{d.date}</span>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-around",alignItems:"center",flexWrap:"wrap",gap:16,paddingTop:4}}>
              <Ring label="Score SEO" score={d.seoScore} color="var(--orange)"/>
              <Ring label="Lisibilité" score={d.readabilityScore} color="var(--green)"/>
              <div style={{textAlign:"center"}}>
                <div style={{fontFamily:"var(--f-display)",fontSize:32,fontWeight:700,color:"var(--orange)",letterSpacing:"-0.03em",marginBottom:6}}>{d.keywordDensity}%</div>
                <div style={{fontSize:11,color:"var(--text3)"}}>Densité</div>
              </div>
            </div>
          </div>

          <div className="card u2" style={{padding:"16px 22px"}}>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:11,color:"var(--text3)",marginRight:4}}>Paramètres :</span>
              <span className="tag tag-o" style={{margin:0}}>Ton : {d.tone}</span>
              <span className="tag tag-n" style={{margin:0}}>{d.length==="court"?"~500 mots":d.length==="moyen"?"~1000 mots":"~1500 mots"}</span>
              {d.audience&&<span className="tag tag-n" style={{margin:0}}>{d.audience}</span>}
            </div>
          </div>

          <div className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="sec" style={{marginBottom:0}}>Titres alternatifs</div>
              <span style={{fontSize:11,color:"var(--text3)"}}>Idéal 50–60 car.</span>
            </div>
            {(d.titles||[]).map((t,i)=>{
              const len=t.length;
              const tc=len>=50&&len<=60?"#1DAF61":len>=30?"#FA600A":"#FB3737";
              return (
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 16px",background:t===d.selectedTitle?"rgba(250,96,10,0.06)":"var(--input-bg)",border:`1.5px solid ${t===d.selectedTitle?"rgba(250,96,10,0.3)":"var(--input-border)"}`,borderRadius:12,marginBottom:8,gap:12}}>
                  <div style={{flex:1,minWidth:0}}>
                    <span style={{fontSize:13,color:t===d.selectedTitle?"var(--orange)":"var(--text)",fontWeight:t===d.selectedTitle?600:400,lineHeight:1.4,display:"block",marginBottom:4}}>
                      {t===d.selectedTitle&&<span style={{marginRight:8,opacity:.6}}>★</span>}{t}
                    </span>
                    <span style={{fontSize:10,fontWeight:700,color:tc}}>{len} car. {len>=50&&len<=60?"✓ Optimal":len<50?"↑ trop court":"↓ trop long"}</span>
                  </div>
                  <CopyBtn text={t} id={`t${i}`}/>
                </div>
              );
            })}
          </div>

          <div className="card">
            <div style={{marginBottom:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div className="sec" style={{marginBottom:0}}>Slug URL</div>
                <CopyBtn text={d.slug||""} id="slug"/>
              </div>
              <div style={{background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:"10px 16px",display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:12,color:"var(--text3)"}}>votre-site.com/</span>
                <span style={{fontSize:13,fontFamily:"var(--f-mono)",color:"var(--orange)",fontWeight:500}}>{d.slug||"votre-article"}</span>
                <span style={{fontSize:12,color:"var(--text3)"}}>/</span>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="sec" style={{marginBottom:0}}>Méta-description</div>
              <CopyBtn text={d.metaDescription||""} id="meta"/>
            </div>
            <div style={{background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:16,fontSize:14,color:"var(--text2)",lineHeight:1.65,marginBottom:10}}>{d.metaDescription}</div>
            <SeoBar value={d.metaDescription?.length||0} min={0} ok1={120} ok2={156} max={175} label="Longueur méta-description"/>
          </div>

          <div className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <div className="sec" style={{marginBottom:0}}>Analyse SEO</div>
              <SeoOverall checks={checks}/>
            </div>
            {checks.map((c,i)=>(<SeoCheckItem key={i} ok={c.ok} warn={c.warn} label={c.label} detail={c.detail}/>))}
          </div>

          <div className="card">
            <div className="sec">Mots-clés sémantiques</div>
            {(d.keywords||[]).map((k,i)=><span key={i} className="tag tag-o">{k}</span>)}
          </div>

          <div className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div className="sec" style={{marginBottom:0}}>Article complet</div>
                {streaming&&(
                  <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(250,96,10,0.08)",border:"1px solid rgba(250,96,10,0.2)",borderRadius:999,padding:"3px 12px"}}>
                    <div className="loader-dot" style={{width:6,height:6}}/>
                    <span style={{fontSize:11,fontWeight:600,color:"var(--orange)"}}>Rédaction en cours…</span>
                  </div>
                )}
              </div>
              {!streaming&&(
                <div style={{display:"flex",gap:8}}>
                  <CopyBtn text={plain} id="art" label="Copier texte"/>
                  <CopyBtn text={d.article||""} id="arthtml" label="Copier HTML"/>
                </div>
              )}
            </div>
            <div className="art" style={{background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:22,minHeight:800,maxHeight:"100%",overflowY:"auto"}}
              dangerouslySetInnerHTML={{__html:(streaming?streamHtml:d.article)||""}}/>
            {streaming&&(<div style={{height:2,borderRadius:999,background:"var(--border)",marginTop:14,overflow:"hidden"}}><div className="loader-bar"/></div>)}
          </div>

          <div className="card" style={{background:"linear-gradient(135deg,rgba(250,96,10,0.04) 0%,rgba(250,96,10,0.01) 100%)",border:"1.5px solid rgba(250,96,10,0.15)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              <div>
                <div style={{fontFamily:"var(--f-display)",fontWeight:700,fontSize:15,color:"var(--text)",marginBottom:4}}>Prêt à publier ?</div>
                <div style={{fontSize:13,color:"var(--text3)"}}>Envoyez cet article directement sur votre WordPress.</div>
              </div>
              <button className="cta-btn green" onClick={()=>setShowWp(true)}>Publier sur WordPress <span className="cta-circle"><WpIco s={16}/></span></button>
            </div>
          </div>

          <div className="card">
            <div className="sec">Visuels suggérés</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:18}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--orange)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:12}}>Prompt IA</div>
                <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.65,marginBottom:14,fontStyle:"italic"}}>"{d.imagePrompt}"</div>
                <CopyBtn text={d.imagePrompt||""} id="imgp" label="Copier le prompt"/>
              </div>
              <div style={{background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:18}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--green)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:12}}>Unsplash</div>
                {(d.unsplashQueries||[]).map((q,i)=>(
                  <a key={i} href={`https://unsplash.com/s/photos/${encodeURIComponent(q)}`} target="_blank" rel="noreferrer"
                    style={{display:"flex",alignItems:"center",gap:8,background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 12px",textDecoration:"none",color:"var(--green)",fontSize:12,fontWeight:600,marginBottom:8}}>↗ {q}</a>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="sec" style={{marginBottom:0}}>Schema.org JSON-LD</div>
              <CopyBtn text={`<script type="application/ld+json">\n${schStr}\n</script>`} id="sch" label="Copier JSON-LD"/>
            </div>
            <pre style={{fontFamily:"var(--f-mono)",background:"var(--input-bg)",border:"1px solid var(--input-border)",borderRadius:12,padding:18,fontSize:12,color:"var(--orange)",overflowX:"auto",maxHeight:200,lineHeight:1.6}}>{schStr}</pre>
          </div>
        </div>
      </div>
    );
  }

  if (page==="performance") {
    const fmtK=n=>n>=1000?(n/1000).toFixed(1)+"k":String(n);
    
    // Format Search Console data to match article structure
    const formatArticles = () => {
      if (scData && scData.rows) {
        return scData.rows.map(row => {
          const url = row.keys[0];
          const path = url.replace(/^https?:\/\/[^\/]+/, '');
          const slug = path.replace(/^\//, '').replace(/\/$/, '');
          
          // Extract title from URL or use URL as title
          let title = slug
            .replace(/[-_]/g, ' ')
            .replace(/\//g, ' - ')
            .replace(/\.html?$/, '')
            .replace(/\s+/g, ' ')
            .trim();
          
          // Capitalize first letter of each word
          title = title.replace(/\b\w/g, l => l.toUpperCase());
          
          return {
            title: title,
            slug: slug,
            clicks28: row.clicks || 0,
            impr28: row.impressions || 0,
            pos: row.position ? row.position.toFixed(1) : "—",
            ctr: row.ctr ? (row.ctr * 100).toFixed(1) : row.clicks > 0 && row.impressions > 0 ? ((row.clicks / row.impressions) * 100).toFixed(1) : "0",
            status: row.impressions > 0 ? "indexed" : "pending",
            trendClicks: [],
            trendImpr: [],
            url: url
          };
        });
      }
      return MOCK_ARTICLES; // Fallback to mock if no real data
    };
    
    const articles = formatArticles();
    const sorted=[...articles]
      .filter(a=>scSearch===""||a.title.toLowerCase().includes(scSearch.toLowerCase())||a.slug.includes(scSearch.toLowerCase()))
      .sort((a,b)=>{
        if (scSort==="clicks") return b.clicks28-a.clicks28;
        if (scSort==="impr")   return b.impr28-a.impr28;
        if (scSort==="ctr")    return (parseFloat(b.ctr)||0)-(parseFloat(a.ctr)||0);
        if (scSort==="pos")    return (parseFloat(a.pos)||99)-(parseFloat(b.pos)||99);
        return 0;
      });
    return (
      <div className={`app ${dark?"dm":"lm"}`}><GlobalStyles/><Nav/>
        <div className="wrap">
          <PageHeader step="Search Console" title="Performance" accent="des articles"
            onBack={()=>setPage("ideas-form")} backLabel="Retour à l'accueil"
            right={scConn?(
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(29,175,97,0.1)",border:"1px solid rgba(29,175,97,0.25)",borderRadius:999,padding:"6px 14px"}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:"#1DAF61"}}/>
                  <span style={{fontSize:12,fontWeight:600,color:"#1DAF61"}}>Connecté · {userEmail}</span>
                </div>
                <button className="ghost-btn" onClick={()=>setScConn(false)} style={{padding:"6px 14px",fontSize:12}}>Déconnecter</button>
              </div>
            ):null}
          />
          {!scConn?(
            <div className="sc-connect-card up">
              <div style={{width:64,height:64,borderRadius:20,background:"rgba(66,133,244,0.1)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",border:"1px solid rgba(66,133,244,0.2)"}}><GscIco s={30}/></div>
              <div style={{fontFamily:"var(--f-display)",fontSize:20,fontWeight:700,color:"var(--text)",letterSpacing:"-0.02em",marginBottom:8}}>Google Search Console</div>
              <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.65,maxWidth:340,margin:"0 auto 28px"}}>
                Connectez votre compte pour suivre les impressions, clics, positions et l'état d'indexation de vos articles.
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:28,textAlign:"left",background:"var(--input-bg)",borderRadius:14,padding:"16px 20px",border:"1px solid var(--border)"}}>
                {[["📈","Clics & impressions par article"],["🎯","Position moyenne dans les SERP"],["✅","Statut d'indexation en temps réel"],["📅","Évolution sur 7, 28 ou 90 jours"]].map(([ico,txt])=>(
                  <div key={txt} style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:"var(--text2)"}}><span>{ico}</span><span>{txt}</span></div>
                ))}
              </div>
              <button className="cta-btn" style={{width:"100%",justifyContent:"center",background:"#4285F4"}} onClick={async()=>{
                try {
                  const response = await fetch('/api/google-auth.php');
                  const data = await response.json();
                  if (data.auth_url) {
                    window.location.href = data.auth_url;
                  }
                } catch (error) {
                  console.error('Error initiating Google auth:', error);
                  // Fallback to demo mode
                  setScConn(true);
                }
              }}>
                <svg width={18} height={18} viewBox="0 0 24 24" style={{marginRight:4}}><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#fff"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff" opacity=".85"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#fff" opacity=".7"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" opacity=".55"/></svg>
                Se connecter avec Google <span className="cta-circle"><ArrowRight s={16}/></span>
              </button>
              <button onClick={()=>setScConn(true)} style={{fontSize:11,color:"var(--text3)",marginTop:14,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Activer le mode démo</button>
            </div>
          ):(
            <>
              {scProperties.length > 0 && (
                <div className="card u1" style={{marginBottom:20,padding:16}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:16}}>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <span style={{fontSize:14,fontWeight:600,color:"var(--text)",fontFamily:"var(--f-display)"}}>Propriété</span>
                      <div style={{position:"relative"}}>
                        <select 
                          value={selectedProperty} 
                          onChange={(e) => {
                            setSelectedProperty(e.target.value);
                            fetchSearchConsoleData(e.target.value);
                          }}
                          style={{
                            background:"var(--input-bg)",
                            border:"1px solid var(--input-border)",
                            borderRadius:8,
                            padding:"10px 32px 10px 12px",
                            fontSize:13,
                            color:"var(--text)",
                            fontWeight:500,
                            minWidth:240,
                            cursor:"pointer",
                            appearance:"none",
                            backgroundImage:"url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"12\" height=\"12\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"%23999\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><polyline points=\"6,9 12,15 18,9\"></polyline></svg>')",
                            backgroundRepeat:"no-repeat",
                            backgroundPosition:"right 10px center",
                            transition:"all 0.2s ease"
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "var(--orange)";
                            e.target.style.boxShadow = "0 0 0 3px rgba(250,96,10,0.1)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "var(--input-border)";
                            e.target.style.boxShadow = "none";
                          }}
                          onMouseEnter={(e) => {
                            if (document.activeElement !== e.target) {
                              e.target.style.borderColor = "var(--orange)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (document.activeElement !== e.target) {
                              e.target.style.borderColor = "var(--input-border)";
                            }
                          }}
                        >
                          {scProperties.map(prop => (
                            <option key={prop.siteUrl} value={prop.siteUrl} style={{color:"var(--text)",background:"var(--card)"}}>
                              {prop.siteUrl.replace(/^https?:\/\//, '')}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:scData ? "#1DAF61" : "var(--text3)"}}/>
                      <span style={{fontSize:12,color:"var(--text3)",fontWeight:500}}>
                        {scData ? `${scData.rows?.length || 0} pages trouvées` : 'Chargement...'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="u1" style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
                {(() => {
                  const displayData = getDisplayData();
                  return [
                    {lbl:`Clics (${scRange}j)`,val:fmtK(displayData.clicks),trend:scData ? "" : "+18%",up:true,color:"var(--orange)",spark:scData ? null : MOCK_GLOBAL.trendClicks},
                    {lbl:"Impressions",val:fmtK(displayData.impressions),trend:scData ? "" : "+24%",up:true,color:"var(--blue)",spark:scData ? null : MOCK_GLOBAL.trendImpr},
                    {lbl:"Position moy.",val:displayData.avgPos,trend:scData ? "" : "-1.2",up:true,color:"#a855f7",spark:null},
                    {lbl:"Pages indexées",val:scData ? `${displayData.indexed}` : `${displayData.indexed}/${displayData.articles.length}`,trend:"",up:true,color:"#1DAF61",spark:null},
                  ];
                })().map(k=>(
                  <div key={k.lbl} className="sc-kpi">
                    <div className="sc-kpi-lbl">{k.lbl}</div>
                    <div className="sc-kpi-val" style={{color:k.color}}>{k.val}</div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8}}>
                      {k.trend?<span style={{fontSize:11,fontWeight:600,color:k.up?"#1DAF61":"#FB3737",display:"flex",alignItems:"center",gap:4}}>{k.up?<TrendUp/>:<TrendDown/>} {k.trend}</span>:<span/>}
                      {k.spark&&<Sparkline data={k.spark} color={k.color} w={80} h={28}/>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="u2" style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:14}}>
                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>Trier par :</span>
                  {[{v:"clicks",l:"Clics"},{v:"impr",l:"Impressions"},{v:"pos",l:"Position"},{v:"ctr",l:"CTR"}].map(s=>(
                    <button key={s.v} className={`sc-tab ${scSort===s.v?"active":""}`} onClick={()=>setScSort(s.v)}>{s.l}</button>
                  ))}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{position:"relative"}}>
                    <input className="inp" style={{paddingLeft:32,height:36,fontSize:12,width:200}} placeholder="Rechercher…" value={scSearch} onChange={e=>setScSearch(e.target.value)}/>
                  </div>
                  <div style={{display:"flex",gap:4}}>
                    {["7","28","90"].map(r=>(<button key={r} className={`range-pill ${scRange===r?"active":""}`} onClick={()=>setScRange(r)}>{r}j</button>))}
                  </div>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 100px 100px 80px 80px",gap:12,padding:"6px 20px",marginBottom:4}}>
                {["Article","Clics","Impressions","CTR","Position"].map((h,i)=>(<div key={h} style={{fontSize:10,fontWeight:700,color:"var(--text3)",textTransform:"uppercase",letterSpacing:"0.08em",textAlign:i===0?"left":"right"}}>{h}</div>))}
              </div>
              {sorted.map((a,i)=>(
                <div key={i} className="sc-row up" style={{animationDelay:`${i*0.05}s`}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 100px 100px 80px 80px",gap:12,alignItems:"center"}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontFamily:"var(--f-display)",fontWeight:600,fontSize:13,color:"var(--text)",lineHeight:1.3,marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.title}</div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:10,color:"var(--text3)",fontFamily:"var(--f-mono)"}}>/{a.slug.replace(/_/g, '-')}/</span>
                        <span style={{fontSize:10,color:"var(--text3)"}}>{a.daysAgo}j</span>
                      </div>
                    </div>
                    <div style={{textAlign:"right",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                      {a.clicks28>0?<><div style={{fontFamily:"var(--f-display)",fontWeight:700,fontSize:15,color:"var(--orange)"}}>{fmtK(a.clicks28)}</div>{a.trendClicks?.length > 0 && <Sparkline data={a.trendClicks} color="var(--orange)" w={60} h={16} fill={false}/>}</>:<span style={{fontSize:12,color:"var(--text3)"}}>—</span>}
                    </div>
                    <div style={{textAlign:"right",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                      {a.impr28>0?<><div style={{fontFamily:"var(--f-display)",fontWeight:700,fontSize:15,color:"var(--blue)"}}>{fmtK(a.impr28)}</div>{a.trendImpr?.length > 0 && <Sparkline data={a.trendImpr} color="var(--blue)" w={60} h={16} fill={false}/>}</>:<span style={{fontSize:12,color:"var(--text3)"}}>—</span>}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <span style={{fontFamily:"var(--f-display)",fontWeight:700,fontSize:14,color:a.ctr==="0"?"var(--text3)":parseFloat(a.ctr)>=3?"#1DAF61":"var(--text)"}}>{a.ctr}%</span>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:"var(--f-display)",fontWeight:700,fontSize:14,color:a.pos==="—"?"var(--text3)":parseFloat(a.pos)<=10?"#1DAF61":parseFloat(a.pos)<=20?"var(--orange)":"var(--text)"}}>{a.pos}</div>
                      {a.up&&a.pos!=="—"&&<TrendUp/>}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`app ${dark?"dm":"lm"}`}><GlobalStyles/><Nav/>
      <div className="wrap">
        <PageHeader step="Articles sauvegardés" title="Historique" onBack={()=>setPage("ideas-form")} backLabel="Retour à l'accueil"/>
        {hist.length===0
          ?<div style={{textAlign:"center",padding:"80px 20px"}}><div style={{fontSize:40,marginBottom:16}}>📭</div><p style={{color:"var(--text3)",fontSize:14}}>Aucun article généré pour l'instant.</p></div>
          :hist.map((h,i)=>(
            <div key={i} className="card" style={{cursor:"pointer",transition:"border-color 0.2s,transform 0.2s"}}
              onClick={()=>{setSelH(h);setPage("generate-result");}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--orange)";e.currentTarget.style.transform="translateY(-1px)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.transform="";}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:16}}>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"var(--f-display)",fontWeight:600,fontSize:15,color:"var(--text)",marginBottom:8,letterSpacing:"-0.015em",lineHeight:1.3}}>{h.selectedTitle||h.keyword}</div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <span className="tag tag-n" style={{margin:0}}>{h.keyword}</span>
                    <span className="tag tag-o" style={{margin:0}}>{h.tone}</span>
                    {h.country&&<span className="tag tag-b" style={{margin:0}}>{h.country}</span>}
                    <span style={{fontSize:11,color:"var(--text3)"}}>{h.date}</span>
                  </div>
                </div>
                <div style={{display:"flex",gap:10,alignItems:"center",flexShrink:0}}>
                  <span className="tag tag-o" style={{margin:0}}>SEO {h.seoScore}</span>
                  <Arr dir="right" fill/>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
